import { useMemo } from 'react'

import { useLatestEntity, useWatchEntityUpdates } from '@domain-group/react-hooks'

import { getQueryParamsFromHash, parsePermittedQueryParams, sortArrayBy } from '@domain-group/javascript-utils'

import useImages from '@hooks/useImages'
import useOrderItems from '@hooks/useOrderItems'
import useOrders from '@hooks/useOrders'
import usePortals from '@hooks/usePortals'
import useSelector from '@hooks/useSelector'

import type { CampaignModel, OrderItemModel } from '@models/types'

import { filterPendingOrderItems } from '@functions/orderItem'

const watchEntityKeys = [
  'orderItems',
  'services',
  'serviceProducts',
  'statuses',
]

function useMediaSelector(initCampaign: Partial<CampaignModel>) {
  // @ts-expect-error TS2322
  const { entity: campaign }: { entity: CampaignModel } = useLatestEntity(initCampaign, 'campaigns')

  const {
    updatedEntities: {
      orderItems: orderItemsUpdatedAt,
      services: servicesUpdatedAt,
      serviceProducts: serviceProductsUpdatedAt,
      statuses: statusesUpdatedAt,
    },
  } = useWatchEntityUpdates(watchEntityKeys)

  const {
    services, serviceProducts, statuses,
  } = useSelector(reduxState => reduxState.entities)

  const orderItemsPayload = useOrderItems({
    filters: {
      campaign_id: campaign.id,
    },
  })

  const {
    filteredOrderItems: orderItems,
  } = orderItemsPayload || {}

  const filteredOrderItems = useMemo((): OrderItemModel[] => {
    const filtered = filterPendingOrderItems({
      orderItems, services, serviceProducts, statuses,
    })
    const sorted = sortArrayBy(filtered, 'asc', 'title')

    return sorted
  }, [campaign.id, orderItemsUpdatedAt, serviceProductsUpdatedAt, servicesUpdatedAt, statusesUpdatedAt])

  const hasFilteredOrderItems = !!filteredOrderItems.length

  const { mediaSelector } = parsePermittedQueryParams(getQueryParamsFromHash(), ['mediaSelector'])
  const isMediaSelector = mediaSelector === 'true'

  const { hasFilteredPortals } = usePortals({ filters: { export_enabled: 'true' }, performHttpRequests: true })

  useImages({
    filters: {
      hidden: 'false',
      imageable_id: campaign.id,
      imageable_type: 'Campaign',
    },
    performHttpRequests: !!campaign.id,
  })

  useOrders({
    filters: {
      campaign_id: campaign.id,
      status_group_key: 'pending',
    },
    requestOptions: {
      include_order_items: true,
      // @ts-expect-error TS2561
      include_order_item_campaign_id: true,
      include_order_item_service_product: true,
      include_order_item_service: true,
    },
    performHttpRequests: !!campaign.id,
  })

  const loading = useSelector((reduxState) => {
    const {
      images, orders, statuses: statusesState,
    } = reduxState

    return images.loading || orders.loading || statusesState.loading
  })

  return {
    hasFilteredPortals,
    filteredOrderItems,
    hasFilteredOrderItems,
    isMediaSelector,
    loading,
  }
}

export default useMediaSelector
